import { useEffect, useRef, useState } from "react";
import { Undo2, Redo2, ImagePlus, Clapperboard, Loader2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { parseEmbed } from "@/lib/richText";

const MODULES = {
  toolbar: [["bold", "italic", "underline"], [{ header: 2 }, { header: 3 }], [{ list: "ordered" }, { list: "bullet" }], ["link", "blockquote"], ["clean"]],
  history: { delay: 800, maxStack: 200, userOnly: true },
};

export const RichEditor = ({ value, onChange, placeholder, testId }) => {
  const quillRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const getEditor = () => {
    const q = quillRef.current;
    return q?.getEditor ? q.getEditor() : q?.editor;
  };

  // Load initial HTML once. ReactQuill's onChange serializes iframes to anchors, so we
  // drive the editor manually: paste the stored HTML in (preserves embeds/images) and
  // read editor.root.innerHTML on every change.
  useEffect(() => {
    const editor = getEditor();
    if (editor && value) {
      editor.clipboard.dangerouslyPasteHTML(value, "silent");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitChange = () => {
    const editor = getEditor();
    if (editor) onChange(editor.root.innerHTML);
  };

  const insertAt = (type, payload) => {
    const editor = getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertEmbed(index, type, payload, "user");
    editor.setSelection(index + 1, 0, "user");
    emitChange();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertAt("image", data.url);
      toast.success("Photo added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onEmbed = () => {
    const url = window.prompt("Paste a YouTube, Instagram or Facebook link:");
    if (!url) return;
    const src = parseEmbed(url);
    if (!src) {
      toast.error("Unsupported link. Use a YouTube, Instagram or Facebook URL.");
      return;
    }
    insertAt("video", src);
    toast.success("Embed added.");
  };

  const undo = () => { getEditor()?.history?.undo(); emitChange(); };
  const redo = () => { getEditor()?.history?.redo(); emitChange(); };

  const btn =
    "flex items-center gap-1.5 border hairline px-3 py-1.5 text-xs font-medium text-foreground/80 hover:text-brand hover:border-brand transition-colors disabled:opacity-50";

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 mb-2">
        <button type="button" data-testid="rt-undo" className={btn} onClick={undo}>
          <Undo2 className="h-3.5 w-3.5" /> Undo
        </button>
        <button type="button" data-testid="rt-redo" className={btn} onClick={redo}>
          <Redo2 className="h-3.5 w-3.5" /> Redo
        </button>
        <button type="button" data-testid="rt-image" className={btn} disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Image
        </button>
        <button type="button" data-testid="rt-embed" className={btn} onClick={onEmbed}>
          <Clapperboard className="h-3.5 w-3.5" /> Video / Social
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onPickImage} />
      </div>
      <div className="bg-white border hairline">
        <ReactQuill ref={quillRef} theme="snow" onChange={emitChange} placeholder={placeholder} modules={MODULES} />
      </div>
    </div>
  );
};
